import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Plus, Trash, FileArrowDown } from "@phosphor-icons/react";
import { downloadCsv } from "./PreTradingTab";
import RuleNote from "./RuleNote";

const VEHICLES = [
  { id: "car_van", label: "Car or van (55p / 25p)" },
  { id: "motorcycle", label: "Motorcycle (24p)" },
  { id: "bicycle", label: "Bicycle (20p)" },
];

export default function UkMileageTab({ onChange }) {
  const [data, setData] = useState({ items: [], ytd_miles: 0, ytd_gbp: 0, total_gbp: 0, unreported_count: 0 });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), purpose: "", from_location: "", to_location: "", distance_miles: "", vehicle: "car_van", passengers: "0", reported_hmrc: false });

  const load = useCallback(async () => { const r = await api.get("/compliance/uk-mileage"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/compliance/uk-mileage", { ...form, distance_miles: Number(form.distance_miles), passengers: Number(form.passengers || 0) });
      setForm({ ...form, purpose: "", from_location: "", to_location: "", distance_miles: "", passengers: "0" });
      load(); onChange && onChange();
    } catch (err) { notifyError(err); }
  };

  const toggle = async (id) => { await api.put(`/compliance/uk-mileage/${id}/reported`); load(); onChange && onChange(); };
  const del = async (id) => { await api.delete(`/compliance/uk-mileage/${id}`); load(); onChange && onChange(); };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6" data-testid="uk-mileage-tab">
        <div className="flex items-center gap-2 eyebrow mb-1">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#5B8DEF22", color: "#5B8DEF", border: "1px solid #5B8DEF" }}>UK</span>
          Log a journey · HMRC AMAP 2026/27
        </div>
        <div className="text-xs text-white/45 mb-4">Cars/vans 55p first 10,000 miles then 25p (cumulative annual). Motorcycles 24p · bicycles 20p. Passenger payments 5p/mile.</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><label>Date</label><input required type="date" data-testid="ukmil-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Purpose</label><input data-testid="ukmil-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Client meeting" /></div>
          <div><label>From</label><input data-testid="ukmil-from" value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} /></div>
          <div><label>To</label><input data-testid="ukmil-to" value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} /></div>
          <div><label>Distance (miles)</label><input required type="number" step="0.1" data-testid="ukmil-miles" value={form.distance_miles} onChange={(e) => setForm({ ...form, distance_miles: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Vehicle</label><select data-testid="ukmil-vehicle" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })}>{VEHICLES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}</select></div>
          <div><label>Passengers</label><input type="number" min="0" step="1" data-testid="ukmil-passengers" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} /></div>
          <button type="submit" data-testid="ukmil-add" className="btn-gold"><Plus weight="bold" size={14} /> Log journey</button>
        </form>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-6 text-sm">
          <div><span className="text-white/50">YTD miles:</span> <span className="mono" data-testid="ukmil-ytd-miles">{Number(data.ytd_miles).toLocaleString()} mi</span></div>
          <div><span className="text-white/50">YTD claim:</span> <span className="mono text-[#FFB020]" data-testid="ukmil-ytd-gbp">£{Number(data.ytd_gbp).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
        </div>
        <button onClick={() => downloadCsv("/compliance/uk-mileage.csv", "uk-mileage-log.csv")} data-testid="ukmil-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date</th><th>Purpose</th><th className="text-right">Miles</th><th>Vehicle</th><th className="text-right">Cum. mi</th><th className="text-right">Pax</th><th className="text-right">Claim</th><th>HMRC</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="9" className="text-center text-white/40 py-8">No journeys logged.</td></tr>
            ) : data.items.map((e) => (
              <tr key={e.entry_id} data-testid={`ukmil-row-${e.entry_id}`}>
                <td className="mono text-xs">{e.date}</td>
                <td>{e.purpose || "—"}</td>
                <td className="num">{Number(e.distance_miles).toFixed(1)}</td>
                <td className="text-white/70 text-xs">{e.vehicle_label}</td>
                <td className="num text-white/60">{Number(e.cumulative_miles).toFixed(0)}</td>
                <td className="num text-white/60">{e.passengers || 0}</td>
                <td className="num text-[#FFB020]">£{Number(e.amount).toFixed(2)}</td>
                <td>
                  <button onClick={() => toggle(e.entry_id)} data-testid={`ukmil-hmrc-${e.entry_id}`} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${e.reported_hmrc ? "badge-pass" : "badge-warn"}`}>{e.reported_hmrc ? "Reported" : "Pending"}</button>
                </td>
                <td><button onClick={() => del(e.entry_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`ukmil-del-${e.entry_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleNote id="uk_mileage" />
    </div>
  );
}
